import {
    Autocomplete,
    Box,
    CircularProgress,
    Divider,
    Stack,
    TextField,
    Typography,
    createFilterOptions,
} from "@mui/material";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLazyListPendingPaymentsForReportsQuery } from "store/services/reportApi";
import { useLazyGetDDLUserListQuery } from "store/services/userApi";
import { BaseTabs } from "components/Shared/BaseTabs";
import ExportPendingPaymentsReportExcel from "./ExportPendingPaymentsReportExcel";

const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Pending Payments Report" },
];

const chargeTypeTabs = [
    { label: "Charge", value: "Charge" },
    { label: "COD", value: "Cash on Delivery" },
];

const filterUserOptions = createFilterOptions({
    stringify: (option) => option.label,
});

const PendingPaymentsReport = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const { viewEntity } = useAuth();

    // Get values from URL or use defaults
    const [responsibleUser, setResponsibleUser] = useState(
        searchParams.get("responsibleUser") || "all"
    );
    const [activeTab, setActiveTab] = useState(
        searchParams.get("tab") || "Charge"
    );

    const [
        fetchPendingPayments,
        { data: priceRes, isLoading, isFetching, error },
    ] = useLazyListPendingPaymentsForReportsQuery();

    const [
        fetchUsers,
        { data: usersData, isLoading: isUsersLoading, isFetching: isUserFetching },
    ] = useLazyGetDDLUserListQuery();

    // Sync state with URL params
    useEffect(() => {
        const tabFromUrl = searchParams.get("tab");
        const userFromUrl = searchParams.get("responsibleUser");

        if (tabFromUrl && tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
        if (userFromUrl && userFromUrl !== responsibleUser) {
            setResponsibleUser(userFromUrl);
        }
    }, [activeTab, responsibleUser, searchParams]);

    // Dynamic columns based on active tab
    const getColumns = () => {
        const baseColumns = [
            {
                name: "Name",
                selector: (row) => row.responsibleUserName,
                cell: (row) => (
                    <Link
                        title={row?.responsibleUserName}
                        to={`/app/crm/reports/delivery-report/user/${row?.responsibleUserId}/detail?chargeType=${activeTab}`}
                        className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                    >
                        {row?.responsibleUserName ? (
                            <HighlightedText text={row.responsibleUserName} />
                        ) : (
                            "N/A"
                        )}
                    </Link>
                ),
                sortable: true,
                grow: 2,
            },
            {
                name: "Payment Type",
                selector: (row) => row?.pendingType,
                cell: (row) => (
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 500,
                            color:
                                row?.pendingType === "Charge"
                                    ? "warning.main"
                                    : "success.main",
                        }}
                    >
                        {row?.pendingType === "Charge"
                            ? "Charge"
                            : "Cash on Delivery"}
                    </Typography>
                ),
                sortable: true,
                grow: 1,
            },
        ];

        // Amount column (dynamic)
        baseColumns.push({
            name:
                activeTab === "Charge"
                    ? "Total Pending Charge"
                    : "Total Pending COD",
            selector: (row) => row?.pendingAmount || 0,
            cell: (row) =>
                row?.pendingAmount
                    ? `Rs. ${Math.abs(row.pendingAmount).toFixed(2)}`
                    : "Rs. 0.00",
            sortable: true,
            right: true,
            grow: 1,
        });

        return baseColumns;
    };


    useEffect(() => {
        fetchUsers({
            account: viewEntity?.entity?.account?._id,
            entity: viewEntity?.entity?._id,
        });
    }, [fetchUsers, viewEntity?.entity?._id, viewEntity?.entity?.account?._id]);

    const userOptions = [
        { label: "All Users", value: "all" },
        ...Array.from(
            new Map(
                (usersData || []).map((user) => {
                    const value = user.value || user._id;
                    return [
                        value,
                        { label: user.label || user.name, value },
                    ];
                })
            ).values()
        ),
    ];

    useEffect(() => {
        const queryParams = {
            responsibleUser: responsibleUser === "all" ? undefined : responsibleUser,
            chargeType: activeTab,
            limit: perPage,
            page: currentPage,
        };

        fetchPendingPayments(queryParams, { preferCacheValue: false });
    }, [currentPage, fetchPendingPayments, perPage, responsibleUser, activeTab]);

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set("tab", newTab);
            params.set("page", "1");
            // Keep the responsibleUser filter
            if (responsibleUser && responsibleUser !== "all") {
                params.set("responsibleUser", responsibleUser);
            }
            return params;
        });
    };

    const handleUserChange = (e, newValue) => {
        const newValueSelected = newValue?.value || "all";
        setResponsibleUser(newValueSelected);
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set("responsibleUser", newValueSelected);
            params.set("page", "1");
            // Keep the current tab
            params.set("tab", activeTab);
            return params;
        });
    };

    return (
        <div>
            <BreadcrumbLayout breadcrumbs={breadcrumbs}>
                <BreadcrumbLayout.Error error={error} />
                <BreadcrumbLayout.Paper>
                    <Box sx={{ p: 2 }}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                flexWrap: "wrap",
                                gap: 1.5,
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6" sx={{ minWidth: 180 }}>
                                Pending Payments Report
                            </Typography>

                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ flex: 1, justifyContent: "flex-end" }}
                            >
                                <Autocomplete
                                    size="small"
                                    options={userOptions}
                                    getOptionLabel={(option) => option.label}
                                    filterOptions={filterUserOptions}
                                    isOptionEqualToValue={(option, value) =>
                                        option.value === value.value
                                    }
                                    renderOption={(props, option) => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <li key={option.value} {...optionProps}>
                                                {option.label}
                                            </li>
                                        );
                                    }}
                                    value={
                                        userOptions.find((opt) => opt.value === responsibleUser) || {
                                            value: "all",
                                            label: "All Users",
                                        }
                                    }
                                    onChange={handleUserChange}
                                    disableClearable
                                    disabled={isUsersLoading || isUserFetching}
                                    sx={{ minWidth: 200 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Responsible User"
                                            autoComplete="off"
                                            inputProps={{
                                                ...params.inputProps,
                                                autoComplete: "new-password",
                                            }}
                                        />
                                    )}
                                />
                                <ExportPendingPaymentsReportExcel
                                    responsibleUser={responsibleUser}
                                    chargeType={activeTab}
                                />
                            </Stack>
                        </Box>

                        <BaseTabs
                            tabs={chargeTypeTabs}
                            activeTab={activeTab}
                            onTabChange={handleTabChange}
                        />

                        <Divider sx={{ mb: 2 }} />

                        {isLoading || isFetching ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : priceRes?.data?.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    No pending {activeTab === "Charge" ? "charges" : "COD"} found for
                                    the selected user
                                </Typography>
                            </Box>
                        ) : (
                            <AppTable
                                columns={getColumns()}
                                data={priceRes?.data ?? []}
                                progressPending={isLoading || isFetching}
                                paginationServer
                                paginationPerPage={perPage}
                                paginationTotalRows={priceRes?.totalItems ?? 0}
                                paginationDefaultPage={currentPage}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handlePerRowsChange}
                            />
                        )}
                    </Box>
                </BreadcrumbLayout.Paper>
            </BreadcrumbLayout>
        </div>
    );
};

export default PendingPaymentsReport;